import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { iconMapping } from 'services/utils/iconMapping';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';
import '../../../style/scss/tooltip.scss';

interface TabSelectProps<T> {
    options: T[];
    selectedOption?: T | null;
    onChange: (selectedValueOrId: string) => void;
    disabled?: boolean;
    errorMessage?: string | string[] | undefined;
    isInvalid?: boolean;
    backendField?: string;
    optionFormat?: string | ((option: T) => string);
    fullWidth?: boolean; // Determines if the tabs of the button are stretched out until the button has it's full width
    buttonSize?: 'normal' | 'large';
    customClass?: string;
    showIconOnly?: boolean;
}

const TabSelect = <T extends { id?: number | string; [key: string]: any }>({
    options, selectedOption, onChange, disabled, errorMessage, isInvalid, backendField,
    optionFormat = 'id', fullWidth = false, buttonSize = 'normal', customClass, showIconOnly,
}: TabSelectProps<T>) => {
    const { t } = useTranslation();
    const { packageFeatures } = useAuthContext();

    // Get the option format, either from a given function or from a string
    const getOptionFormat = (option: T): string => {
        if (typeof optionFormat === 'function') {
            return optionFormat(option);
            
        } else if (typeof optionFormat === 'string') {
            const value = option[optionFormat];
            return typeof value === 'string' ? value : '';
        }

        return 'Unknown optionFormat';
    };

    return (
        <div className={`tabs-field tab-select ${buttonSize} ${showIconOnly ? 'icon-only' : ''}`}>
            {options.map((option: T, index) => {

                // Determine the active option
                const isActive = selectedOption
                    ? (
                        (backendField && option[backendField] && selectedOption[backendField] === option[backendField]) ||
                        (option.id && selectedOption.id === option.id) || 
                        (option.value && selectedOption.value === option.value))
                    : false;

                // Determine if the button is disabled when the optionally required feature is not included in the environment package
                const disabledByRequiredFeature = option.requiredFeature && !packageFeatures.includes(option.requiredFeature)

                return (
                    <button key={index}
                            disabled={disabledByRequiredFeature || disabled}
                            className={`${customClass ? customClass : ''} ${option.value} ${option.icon && showIconOnly ? 'tooltip-icon' : 'icon-text'} ${disabledByRequiredFeature ? 'tooltip-icon' : ''} ${isActive ? 'active' : ''} ${fullWidth ? 'stretched' : ''} ${errorMessage || isInvalid ? 'is-invalid' : ''}`}
                            onClick={() => { onChange(String(backendField && option[backendField] || option.id || option.value)) }}>
                        {/* If an icon is given, show the icon */}
                        {option.icon && 
                            <>
                                <FontAwesomeIcon icon={iconMapping(option.icon)} className='tab-option-icon' />
                                {/* If only the icon should be shown, give the name in a tooltip */}
                                {showIconOnly &&
                                    <span className="tooltip">{getOptionFormat(option)}</span>
                                }
                            </>
                        }
                        {/* Show the name if it's not a showIconOnly button */}
                        {!showIconOnly && 
                            t(getOptionFormat(option))
                        }
                        {/* Show the count of the option as a round badge in the given color class */}
                        {option.count !== undefined && option.count !== null &&
                            <span className={`tab-count ${option.countClass ?? ''}`}>{option.count}</span>
                        }
                        {/* Show a tooltip above the button if it's not available in the environments package */}
                        {disabledByRequiredFeature &&
                            <span className="tooltip">{t('billing.option_not_available_tooltip')}</span>
                        }
                    </button>
                );
            })}
        </div>   
    );
};

export default TabSelect;